import { css } from '@emotion/react';
import { FC, useState, useEffect } from 'react';
import { getEtfBottomLine, GetEtfBottomLineResponse, getBasicInvestment } from '../../../apis/backend';
import { colors, fontStyles, spacing } from '../../../styles';
import SectionHeading from '../SectionHeading';
import Indicator, { Size } from '../../RatingIndicator';
import { reportError } from '../../../utils/reporting';
import Info from '../Info';

interface Props {
  symbol: string;
}

const Overview: FC<Props> = ({ symbol }) => {
  const [data, setData] = useState<GetEtfBottomLineResponse | undefined>();
  const [investmentName, setInvestmentName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const [bottomLineResult, basicInfoResult] = await Promise.allSettled([
          getEtfBottomLine(symbol),
          getBasicInvestment(symbol),
        ]);

        // Extract data from fulfilled promises, gracefully handle failures
        const bottomLineData = bottomLineResult.status === 'fulfilled' ? bottomLineResult.value : undefined;
        const basicInfo = basicInfoResult.status === 'fulfilled' ? basicInfoResult.value : undefined;

        if (bottomLineData) {
          setData(bottomLineData);
        }
        if (basicInfo) {
          setInvestmentName(basicInfo.name);
          setCategory(basicInfo.category);
          setDescription(basicInfo.description ?? bottomLineData?.description ?? '');
        } else if (bottomLineData) {
          // Fallback to bottomLineData description if basicInfo fails
          setDescription(bottomLineData.description ?? '');
        }

        // Report errors but don't block rendering
        if (bottomLineResult.status === 'rejected') {
          reportError(
            bottomLineResult.reason instanceof Error
              ? bottomLineResult.reason
              : new Error(String(bottomLineResult.reason)),
          );
        }
        if (basicInfoResult.status === 'rejected') {
          reportError(
            basicInfoResult.reason instanceof Error
              ? basicInfoResult.reason
              : new Error(String(basicInfoResult.reason)),
          );
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [symbol]);

  const hasValidBottomLineData = data?.details.some((detail) => detail.rating !== null && detail.description !== null);
  const hasAboutSection = description?.trim() && investmentName && category;

  // Show nothing if no data at all
  if (!isLoading && !hasValidBottomLineData && !hasAboutSection) {
    return null;
  }

  const headingMapping: { [index: number]: string } = {
    0: 'return',
    1: 'risk',
    2: 'size',
    3: 'fees',
  };

  return (
    <div>
      {hasAboutSection && (
        <Info name={investmentName} category={category} description={description} isEtfOrMutualFund={true} />
      )}
      {hasValidBottomLineData && (
        <>
          <SectionHeading text="Bottom Line" />
          <ul
            css={css`
              margin: 0;
              list-style: none;
              position: relative;
              padding-left: ${spacing.hecto};
            `}
          >
            {data!.details.map(({ title, description, rating }, index) =>
              rating !== null && description !== null ? (
                <li key={index}>
                  <div
                    css={css`
                      position: absolute;
                      left: 0;
                      margin-top: -4px;
                    `}
                  >
                    <Indicator heading={headingMapping[index]} rating={rating} size={Size.large} />
                  </div>
                  <h3
                    css={css`
                      margin: 0;
                      ${fontStyles.lion}
                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {title}
                  </h3>
                  <p
                    css={css`
                      margin: ${spacing.centi} 0 ${spacing.regular} 0;
                      ${fontStyles.mouse}
                      html[data-theme='dark'] & {
                        color: ${colors.sugar};
                      }
                    `}
                  >
                    {description}
                  </p>
                </li>
              ) : null,
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default Overview;
